/**
 * Scan a label, hand the tool over or take it back, scan the next one.
 *
 * The screen the office uses standing at the store door with a phone, which is why it
 * is one column, why the buttons are large, and why it returns to the camera the moment
 * a handover is recorded rather than leaving you on a detail page to navigate out of.
 *
 * ## It does not ask what you want to do
 *
 * The item's own state already says. Something in the store can only go out; something
 * that is out can only come back. Offering both would be offering one wrong answer, and
 * the wrong one here writes a second open row against a tool that already has a holder.
 *
 * ## Resolution stays server-side
 *
 * The camera hands back whatever was encoded — our URL, our code, or the manufacturer's
 * barcode. `GET /inventory/scan/:code` unpicks all three, exactly as it does for a
 * camera app pointed at the same sticker. Nothing here parses a code.
 */

import { useCallback, useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { CodeScanner } from '@/components/CodeScanner';
import { Badge, Card, ErrorNotice, Field, PageHeader, Spinner } from '@/components/ui';
import { InventoryPhoto } from '@/components/InventoryPhoto';
import { useI18n } from '@/i18n';
import { api, type InventoryItem, type InventoryStatus } from '@/lib/api';
import { dateShort, isoDate } from '@/lib/format';
import { useLabel } from '@/lib/useLabel';
import { Combobox } from '@/components/Combobox';
import { personOptions, siteOptions } from '@/features/access/people';

const STATUS_TONE: Record<
  InventoryStatus,
  'neutral' | 'primary' | 'success' | 'warning' | 'danger'
> = {
  available: 'success',
  assigned: 'primary',
  maintenance: 'warning',
  lost: 'danger',
  retired: 'neutral',
};

/** The statuses an item can come back AS. `assigned` is never one of them. */
const RETURN_STATUSES: InventoryStatus[] = ['available', 'maintenance', 'lost'];

export function InventoryQuickScanPage() {
  const { t } = useI18n();
  const L = useLabel();
  const queryClient = useQueryClient();

  const [code, setCode] = useState<string | null>(null);
  const [typed, setTyped] = useState('');
  const [holder, setHolder] = useState('');
  const [dueBackOn, setDueBackOn] = useState('');
  const [note, setNote] = useState('');
  const [backStatus, setBackStatus] = useState<InventoryStatus>('available');

  const users = useQuery({ queryKey: ['users'], queryFn: () => api.users() });
  const sites = useQuery({ queryKey: ['sites'], queryFn: api.sites });

  const found = useQuery({
    queryKey: ['inventory-scan', code],
    queryFn: () => api.scanInventory(code!),
    enabled: code !== null,
    retry: false,
  });

  const item: InventoryItem | undefined = found.data?.item;

  const reset = () => {
    setCode(null);
    setTyped('');
    setHolder('');
    setDueBackOn('');
    setNote('');
    setBackStatus('available');
  };

  const after = async () => {
    await queryClient.invalidateQueries({ queryKey: ['inventory'] });
    reset();
  };

  const handOver = useMutation({
    mutationFn: () => {
      const [kind, value] = holder.split(':');
      return api.checkOutItem(item!.id, {
        userId: kind === 'user' ? value : undefined,
        siteId: kind === 'site' ? value : undefined,
        dueBackOn: dueBackOn || undefined,
        notes: note.trim() || undefined,
      });
    },
    onSuccess: after,
  });

  const takeBack = useMutation({
    mutationFn: () =>
      api.checkInItem(item!.id, { status: backStatus, notes: note.trim() || undefined }),
    onSuccess: after,
  });

  /*
   * Stable, so the scanner is not torn down and rebuilt on every keystroke in the note
   * box. A restarting camera flickers, and on some phones re-prompts for permission.
   */
  const onCode = useCallback((next: string) => setCode(next), []);

  return (
    <>
      <PageHeader
        title={t('inventory.scanTitle')}
        subtitle={t('inventory.subtitle')}
        actions={
          <Link to="/inventory" className="btn-secondary">
            {t('inventory.title')}
          </Link>
        }
      />

      <div className="max-w-xl mx-auto space-y-4">
        {!code ? (
          <>

            <Card>
              <CodeScanner onCode={onCode} />
            </Card>

            {/* Always available. A scratched label, a tool in a dark corner, a phone
                with no camera — all normal, and all dead ends without this. */}
            <Card>
              <Field label={t('inventory.scanManual')}>
                <div className="flex gap-2">
                  <input
                    className="input tabular"
                    value={typed}
                    onChange={(e) => setTyped(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && typed.trim() && setCode(typed.trim())}
                    placeholder="HH-INV-0042"
                  />
                  <button
                    type="button"
                    className="btn-secondary shrink-0"
                    disabled={!typed.trim()}
                    onClick={() => setCode(typed.trim())}
                  >
                    {t('inventory.scanManualAction')}
                  </button>
                </div>
              </Field>
            </Card>
          </>
        ) : found.isLoading ? (
          <Spinner label={t('inventory.scanLooking')} />
        ) : found.error ? (
          <Card>
            <h2 className="font-bold mb-1">{t('inventory.scanFailed')}</h2>
            <p className="text-sm text-muted mb-4">{t('inventory.scanFailedHint', { code })}</p>
            <ErrorNotice error={found.error} />
            <button type="button" className="btn-primary mt-4" onClick={reset}>
              {t('inventory.scanAgain')}
            </button>
          </Card>
        ) : item ? (
          <>
            <Card className="flex gap-3">
              <InventoryPhoto
                url={item.photoUrl}
                alt={t('inventory.photoOf', { name: item.name })}
                size={56}
              />
              <div className="min-w-0 flex-1">
                <Link
                  to={`/inventory/${item.id}`}
                  className="font-bold hover:underline block truncate"
                >
                  {item.name}
                </Link>
                <div className="text-xs text-subtle tabular">{item.code}</div>
                <div className="mt-2">
                  <Badge tone={STATUS_TONE[item.status]}>
                    {t(`inventory.status_${item.status}` as 'inventory.status_available')}
                  </Badge>
                </div>
              </div>
            </Card>

            {item.holder ? (
              /* Out with somebody: the only thing to do is take it back. */
              <Card className="space-y-4">
                <div>
                  <h2 className="font-bold">
                    {t('inventory.scanOut', { holder: item.holder.name ?? '' })}
                  </h2>
                  <p className="text-sm text-muted mt-0.5">
                    {t('inventory.scanOutHint', {
                      date: item.checkedOutAt ? dateShort(item.checkedOutAt) : '',
                    })}
                  </p>
                </div>

                {/* It comes back AS something. A meter returned in pieces is not
                    available, and assuming it is puts it in the next person's hands. */}
                <Field
                  label={t('common.status')}
                  hint={t(`inventory.statusHint_${backStatus}` as 'inventory.statusHint_available')}
                >
                  <select
                    className="input"
                    value={backStatus}
                    onChange={(e) => setBackStatus(e.target.value as InventoryStatus)}
                  >
                    {RETURN_STATUSES.map((s) => (
                      <option key={s} value={s}>
                        {t(`inventory.status_${s}` as 'inventory.status_available')}
                      </option>
                    ))}
                  </select>
                </Field>

                <Field label={t('common.notes')}>
                  <textarea
                    className="input h-16 py-2 resize-none"
                    value={note}
                    onChange={(e) => setNote(e.target.value)}
                  />
                </Field>

                {takeBack.error ? <ErrorNotice error={takeBack.error} /> : null}

                <div className="flex gap-2">
                  <button
                    type="button"
                    className="btn-primary flex-1"
                    disabled={takeBack.isPending}
                    onClick={() => takeBack.mutate()}
                  >
                    {takeBack.isPending ? t('common.saving') : t('inventory.handBack')}
                  </button>
                  <button type="button" className="btn-secondary" onClick={reset}>
                    {t('inventory.scanAgain')}
                  </button>
                </div>
              </Card>
            ) : item.status === 'retired' || item.status === 'lost' ? (
              /* Nothing to hand out. Saying so beats a form that fails on submit. */
              <Card className="space-y-3">
                <p className="text-sm">
                  {t(`inventory.statusHint_${item.status}` as 'inventory.statusHint_lost')}
                </p>
                <button type="button" className="btn-primary" onClick={reset}>
                  {t('inventory.scanAgain')}
                </button>
              </Card>
            ) : (
              /* In the store: hand it to somebody. */
              <Card className="space-y-4">
                <div>
                  <h2 className="font-bold">{t('inventory.scanReady')}</h2>
                  <p className="text-sm text-muted mt-0.5">{t('inventory.scanReadyHint')}</p>
                </div>

                {/* One picker for people and sites, with a prefixed value. Two would
                    let somebody fill in both, and the server would then have to decide
                    which one wins — a decision nobody should be making. */}
                <Field label={t('inventory.holder')}>
                  <Combobox
                    value={holder}
                    onChange={setHolder}
                    options={[
                      ...personOptions(users.data?.users ?? [], L, t('inventory.holderPerson')).map((o) => ({ ...o, value: `user:${o.value}` })),
                      ...siteOptions(sites.data?.sites ?? [], t('inventory.holderSite')).map((o) => ({ ...o, value: `site:${o.value}` })),
                    ]}
                  />
                </Field>

                <Field label={t('inventory.dueBack')} hint={t('inventory.dueBackHint')}>
                  <input
                    type="date"
                    className="input"
                    min={isoDate(new Date())}
                    value={dueBackOn}
                    onChange={(e) => setDueBackOn(e.target.value)}
                  />
                </Field>

                <Field label={t('common.notes')}>
                  <textarea
                    className="input h-16 py-2 resize-none"
                    value={note}
                    onChange={(e) => setNote(e.target.value)}
                  />
                </Field>

                {handOver.error ? <ErrorNotice error={handOver.error} /> : null}

                <div className="flex gap-2">
                  <button
                    type="button"
                    className="btn-primary flex-1"
                    disabled={!holder || handOver.isPending}
                    onClick={() => handOver.mutate()}
                  >
                    {handOver.isPending ? t('common.saving') : t('inventory.handOver')}
                  </button>
                  <button type="button" className="btn-secondary" onClick={reset}>
                    {t('inventory.scanAgain')}
                  </button>
                </div>
              </Card>
            )}
          </>
        ) : null}
      </div>
    </>
  );
}
